import { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

interface Reminder {
  id: string;
  title: string;
  scheduled_date: string;
  reminder_type: string;
  priority: string;
  parcel_id: string | null;
}

export const NotificationBell = () => {
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUpcomingReminders();

    // Subscribe to realtime updates
    const channel = supabase
      .channel('reminder-notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'field_reminders'
        },
        () => {
          fetchUpcomingReminders();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchUpcomingReminders = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const now = new Date();
      const threeDaysFromNow = new Date();
      threeDaysFromNow.setDate(now.getDate() + 3);

      const { data, error } = await supabase
        .from('field_reminders')
        .select('id, title, scheduled_date, reminder_type, priority, parcel_id')
        .eq('user_id', user.id)
        .eq('is_completed', false)
        .gte('scheduled_date', now.toISOString())
        .lte('scheduled_date', threeDaysFromNow.toISOString())
        .order('scheduled_date', { ascending: true })
        .limit(5);

      if (error) throw error;
      setReminders(data || []);
    } catch (error) {
      console.error('Error fetching reminders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getReminderIcon = (type: string) => {
    switch (type) {
      case 'irrigation': return '💧';
      case 'fertilization': return '🌱';
      case 'treatment': return '🧪';
      case 'harvest': return '🌾';
      case 'inspection': return '👁️';
      default: return '📋';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {reminders.length > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {reminders.length}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 max-w-[90vw]">
        <DropdownMenuLabel>Upcoming Tasks</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {loading ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            Loading...
          </div>
        ) : reminders.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            No upcoming tasks in the next 3 days
          </div>
        ) : (
          <>
            {reminders.map((reminder) => (
              <DropdownMenuItem
                key={reminder.id}
                className="cursor-pointer flex flex-col items-start p-3 gap-1"
                onClick={() => navigate('/dashboard/calendar')}
              >
                <div className="flex items-center gap-2 w-full">
                  <span className="text-lg flex-shrink-0">{getReminderIcon(reminder.reminder_type)}</span>
                  <span 
                    className="font-medium flex-1 text-sm truncate max-w-[220px]" 
                    title={reminder.title}
                  >
                    {reminder.title}
                  </span>
                  <Badge variant="outline" className={`text-xs flex-shrink-0 ${getPriorityColor(reminder.priority)}`}>
                    {reminder.priority}
                  </Badge>
                </div>
                <span className="text-xs text-muted-foreground ml-7">
                  {formatDistanceToNow(new Date(reminder.scheduled_date), { addSuffix: true })}
                </span>
              </DropdownMenuItem>
            ))}
            
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-center justify-center cursor-pointer text-primary"
              onClick={() => navigate('/dashboard/calendar')}
            >
              View All Tasks
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
